import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  ExternalLink,
  Building2,
  Mail,
  Phone,
  Calendar,
  FileText,
  MessageCircle,
  DollarSign,
  User,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { dealsApi, type DealStage, type DealActivity } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DealStageBadge,
  DEAL_STAGE_CONFIG,
  DEAL_STAGES,
} from '@/components/deal-stage-badge'
import { AddDealActivityForm } from '@/components/add-deal-activity-form'
import { cn } from '@/lib/utils'

const ACTIVITY_ICONS: Record<string, typeof Mail> = {
  note: FileText,
  email_sent: Mail,
  email_received: Mail,
  call: Phone,
  meeting: Calendar,
  stage_change: MessageCircle,
}

function ActivityItem({ activity }: { activity: DealActivity }) {
  const Icon = ACTIVITY_ICONS[activity.activity_type] ?? FileText
  const timeAgo = activity.created_at
    ? formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })
    : ''

  return (
    <div className="flex gap-3">
      <div className="shrink-0 mt-1">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm capitalize">
            {activity.activity_type.replace(/_/g, ' ')}
          </span>
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
        </div>
        {activity.content && (
          <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">
            {activity.content}
          </p>
        )}
      </div>
    </div>
  )
}

export default function DealDetailPage() {
  const { id: dealId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: deal, isLoading } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealsApi.get(dealId!),
    enabled: !!dealId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: { stage?: DealStage; expected_value?: number }) =>
      dealsApi.update(dealId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deal-stage-counts'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Deal not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/app/deals')}
        >
          Back to Deals
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/app/deals')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{deal.title}</h1>
          {deal.campaign_name && (
            <p className="text-muted-foreground">
              Campaign: {deal.campaign_name}
            </p>
          )}
        </div>
        <DealStageBadge stage={deal.stage} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deal.company ? (
                <div className="space-y-2">
                  <div className="font-medium text-lg">
                    {deal.company.name}
                  </div>
                  {deal.company.website && (
                    <a
                      href={
                        deal.company.website.startsWith('http')
                          ? deal.company.website
                          : `https://${deal.company.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      {deal.company.website}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {deal.company.country && (
                    <div className="text-sm text-muted-foreground">
                      {deal.company.country}
                    </div>
                  )}
                  {deal.company.business_type && (
                    <Badge variant="outline">{deal.company.business_type}</Badge>
                  )}
                  <div className="pt-2">
                    <Link
                      to={`/app/companies/${deal.raw_company_id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      View full company profile
                    </Link>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No company data</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <AddDealActivityForm dealId={dealId!} />
              <Separator />
              {deal.activities.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No activities yet
                </p>
              ) : (
                <div className="space-y-4">
                  {deal.activities.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Deal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select
                  value={deal.stage}
                  onValueChange={(v) =>
                    updateMutation.mutate({ stage: v as DealStage })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        <span className="flex items-center gap-2">
                          {DEAL_STAGE_CONFIG[stage].label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Expected Value</Label>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="Enter value"
                    defaultValue={deal.expected_value ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined
                      if (value !== deal.expected_value) {
                        updateMutation.mutate({ expected_value: value })
                      }
                    }}
                  />
                </div>
              </div>

              {deal.created_at && (
                <div className="text-sm text-muted-foreground">
                  Created{' '}
                  {formatDistanceToNow(new Date(deal.created_at), {
                    addSuffix: true,
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deal.contact_name || deal.contact_email ? (
                <div className="space-y-2">
                  {deal.contact_name && (
                    <div className="font-medium">{deal.contact_name}</div>
                  )}
                  {deal.contact_email && (
                    <a
                      href={`mailto:${deal.contact_email}`}
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      <Mail className="h-3 w-3" />
                      {deal.contact_email}
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">No contact info</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
